/**
 * services/client.ts
 * Shared helpers for the service layer: unwrap the `{ data, meta }` envelope,
 * detect a true connectivity failure, and run a call with a demo-seed fallback.
 */
import axiosInstance from '../api-endpoints/axiosInstance';
import { isDemoMode } from './_demo/demoMode';

export { axiosInstance };

/** Unwrap `{ data }` envelopes; pass through already-bare payloads. */
export function unwrap<T>(responseData: unknown): T {
  if (responseData && typeof responseData === 'object' && 'data' in (responseData as object)) {
    return (responseData as { data: T }).data;
  }
  return responseData as T;
}

/** True only for transport failures (server unreachable) — NOT 4xx/5xx. */
export function isOffline(error: unknown): boolean {
  return (
    !!error && typeof error === 'object' && !('response' in (error as object) && (error as { response?: unknown }).response)
  );
}

/**
 * Run a live API call, falling back to the demo seed when the platform is
 * unavailable. In demo mode the seed is returned WITHOUT a network call (so no
 * stray 401s); otherwise the live call runs and only a transport failure (not a
 * real 4xx/5xx) falls back. This is the single place that fallback lives —
 * every service reuses it, so the behavior is identical everywhere.
 */
export async function withFallback<T>(live: () => Promise<T>, seed: () => T): Promise<T> {
  if (isDemoMode()) return seed();
  try {
    return await live();
  } catch (e) {
    if (isOffline(e)) return seed();
    throw e;
  }
}

/**
 * Like withFallback but for WRITES (reject / delete / activate / restore …).
 * In demo mode the write is a no-op success (no backend). Live, it does NOT
 * swallow a transport failure as fake success — a failed write must throw so
 * the caller reverts its optimistic update AND surfaces the error, instead of
 * pretending it saved and then silently snapping back on the next refetch.
 */
export async function runWrite<T>(live: () => Promise<T>, demoResult: () => T): Promise<T> {
  if (isDemoMode()) return demoResult();
  return live();
}
